'use client';

import { getAllowanceKey, type OnUpdate, type TokenAllowanceData } from '@revoke.cash/core/allowances';
import { walletSupportsEip5792 } from '@revoke.cash/core/eip5792';
import {
  isAccountUpgradeRejectionError,
  isSwitchChainNotSupportedError,
  isUserRejectionError,
  parseErrorMessage,
} from '@revoke.cash/core/utils/errors';
import { getFeeDollarAmount } from 'components/allowances/controls/batch-revoke/fee';
import { useAddress } from 'lib/hooks/page-context/AddressIdentityContext';
import { useCallback, useEffect, useMemo } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { toast } from 'react-toastify';
import { isTransactionStatusLoadingState, useTransactionStore } from '../../stores/transaction-store';
import { useEnsureWalletClient } from './ensureWalletClient';
import { useNativeTokenPrice } from './useNativeTokenPrice';
import { useRevokeBatchEip5792 } from './useRevokeBatchEip5792';
import { useRevokeBatchQueuedTransactions } from './useRevokeBatchQueuedTransactions';
import { useWalletCapabilities } from './useWalletCapabilities';

export const useRevokeBatch = (allowances: TokenAllowanceData[], onUpdate: OnUpdate) => {
  // Get chainId from the first allowance (all selected allowances should be from the same chain)
  const chainId = allowances[0]?.chainId ?? 1;
  const { isPremium } = useAddress();

  const { results, getTransaction, updateTransaction } = useTransactionStore();
  const walletCapabilities = useWalletCapabilities(chainId);
  const batchAtomic = useRevokeBatchEip5792(allowances, onUpdate);
  const batchQueued = useRevokeBatchQueuedTransactions(allowances, onUpdate);
  const { nativeTokenPrice } = useNativeTokenPrice(chainId);

  // If we cannot get the native token price, we set the fee to $0 (this will result in no fee payment downstream)
  const feeDollarAmount = nativeTokenPrice ? getFeeDollarAmount(chainId, allowances.length, isPremium).toFixed(2) : '0';

  const { ensureWalletClient } = useEnsureWalletClient();

  useEffect(() => {
    allowances.forEach((allowance) => {
      updateTransaction(getAllowanceKey(allowance), { status: 'not_started' }, false);
    });
  }, [allowances, updateTransaction]);

  const { execute: revoke, loading: isSubmitting } = useAsyncCallback(async (): Promise<void> => {
    try {
      const walletClient = await ensureWalletClient(chainId);

      const supportsEip5792 = walletCapabilities.isLoading
        ? await walletSupportsEip5792(walletClient, chainId)
        : walletCapabilities.supportsEip5792;

      if (supportsEip5792 && hasMoreThanOneTransaction(allowances, feeDollarAmount)) {
        try {
          await batchAtomic.revoke(feeDollarAmount);
        } catch (error) {
          // Fall back to queued transactions if the user rejected the account upgrade
          if (isAccountUpgradeRejectionError(error)) {
            await batchQueued.revoke(feeDollarAmount);
            return;
          }

          throw error;
        }
      } else {
        await batchQueued.revoke(feeDollarAmount);
      }
    } catch (error) {
      // Rejecting the chain switch prompt is intentional, and switch failures already get their own toast
      if (!isUserRejectionError(error) && !isSwitchChainNotSupportedError(error)) {
        toast.error(parseErrorMessage(error));
      }
      throw error;
    }
  });

  const pause = useCallback(() => {
    batchAtomic.pause();
    batchQueued.pause();
  }, [batchAtomic, batchQueued]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(results): updated results mean the memo is stale
  const relevantResults = useMemo(() => {
    return Object.fromEntries(
      allowances.map((allowance) => [getAllowanceKey(allowance), getTransaction(getAllowanceKey(allowance))]),
    );
  }, [allowances, getTransaction, results]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(results): updated results mean the memo is stale
  const isRevoking = useMemo(() => {
    return allowances.some((allowance) =>
      isTransactionStatusLoadingState(getTransaction(getAllowanceKey(allowance)).status),
    );
  }, [allowances, getTransaction, results]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(results): updated results mean the memo is stale
  const isAllConfirmed = useMemo(() => {
    return allowances.every((allowance) => getTransaction(getAllowanceKey(allowance)).status === 'confirmed');
  }, [allowances, getTransaction, results]);

  return {
    revoke,
    pause,
    results: relevantResults,
    isSubmitting,
    isRevoking,
    isAllConfirmed,
    feeDollarAmount,
    progress: batchQueued.progress,
  };
};

const hasMoreThanOneTransaction = (allowances: TokenAllowanceData[], feeDollarAmount: string) => {
  if (allowances.length === 0) return false;
  if (allowances.length === 1 && Number(feeDollarAmount) === 0) return false;
  return true;
};
